import { useMemo, useState } from 'react'
import { useApp } from '../store/AppContext'
import { HANDOVER_FIELDS, emptyHandoverNote, isHandoverEmpty } from '../lib/handover'
import { localISODate } from '../lib/date'
import { formatTime12Hour } from '../lib/time'
import type { HandoverField } from '../lib/handover'
import type { HandoverNote } from '../types'

export function InlineHandoverWidget() {
  const { currentUser, recordHandover, myLinkableShifts } = useApp()
  const todayISO = localISODate()
  const defaultShiftId = useMemo(
    () => myLinkableShifts.find((shift) => shift.date === todayISO)?.id ?? myLinkableShifts[0]?.id ?? '',
    [myLinkableShifts, todayISO],
  )
  const [expanded, setExpanded] = useState(false)
  const [linkedEventId, setLinkedEventId] = useState(defaultShiftId)
  const [note, setNote] = useState<HandoverNote>(emptyHandoverNote)
  const [status, setStatus] = useState('')

  if (currentUser.role !== 'support_worker') return null

  const setField = (key: HandoverField, value: string) => setNote((current) => ({ ...current, [key]: value }))

  const save = () => {
    if (isHandoverEmpty(note)) {
      setStatus('Add at least one handover note before saving.')
      return
    }
    const ok = recordHandover({
      note,
      documentationStyle: 'standard',
      linkedEventId: linkedEventId || undefined,
      notifyBySms: false,
    })
    if (!ok) {
      setStatus('This handover could not be saved.')
      return
    }
    setNote(emptyHandoverNote())
    setStatus('Saved ✓')
    setExpanded(false)
  }

  return (
    <section className="rounded-2xl border border-brand-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-extrabold text-slate-900">🔄 Shift Handover</p>
          <p className="mt-1 text-xs text-slate-500">Write and save your handover here before you finish.</p>
        </div>
        <button type="button" onClick={() => setExpanded((value) => !value)} className="rounded-xl bg-brand-50 px-3 py-2 text-xs font-bold text-brand-700">
          {expanded ? 'Close' : 'Write handover'}
        </button>
      </div>

      {status && <p className={`mt-3 rounded-xl px-3 py-2 text-xs font-semibold ${status.startsWith('Saved') ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>{status}</p>}

      {expanded && (
        <div className="mt-4 flex flex-col gap-3">
          {myLinkableShifts.length > 0 && (
            <label className="text-xs font-semibold text-slate-600">
              <span className="mb-1 block">Shift</span>
              <select value={linkedEventId} onChange={(event) => setLinkedEventId(event.target.value)} className="input">
                <option value="">Not linked to a shift</option>
                {myLinkableShifts.map((shift) => (
                  <option key={shift.id} value={shift.id}>{shift.date} · {formatTime12Hour(shift.startTime)}–{formatTime12Hour(shift.endTime)} · {shift.title}</option>
                ))}
              </select>
            </label>
          )}

          {HANDOVER_FIELDS.map((field) => (
            <label key={field.key} className="text-xs font-semibold text-slate-600">
              <span className="mb-1 block">{field.label}</span>
              <textarea value={note[field.key]} onChange={(event) => setField(field.key, event.target.value)} rows={field.rows > 2 ? 2 : field.rows} className="input" placeholder={field.placeholder} />
            </label>
          ))}

          <button type="button" onClick={save} className="rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">Save handover</button>
          <p className="text-center text-[11px] text-slate-400">Saved in Marnixia and shared through Team Updates with authorised people only.</p>
        </div>
      )}
    </section>
  )
}

